/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useState, useMemo, Fragment, forwardRef, useContext } from 'react';
import PropTypes from "prop-types";
import { EuiLoadingSpinner } from '../loading';
import { EuiButton, EuiButtonEmpty, EuiButtonIcon } from '../button';
import { EuiTitle } from '../title';
import { EuiModal, EuiModalBody, EuiModalFooter, EuiModalHeader } from '../modal';
import { EuiI18n, useEuiI18n } from '../i18n';
import { EuiPopover, EuiPopoverTitle } from '../popover';
import { EuiText } from '../text';
import { EuiSpacer } from '../spacer';
import { EuiToolTip } from '../tool_tip';
// @ts-ignore a react svg
import MarkdownLogo from './icons/markdown_logo';
import { EuiHorizontalRule } from '../horizontal_rule';
import { EuiLink } from '../link';
import { EuiMarkdownContext } from './markdown_context';
export var EuiMarkdownEditorFooter = forwardRef((props, ref) => {
  const {
    uiPlugins,
    isUploadingFiles,
    openFiles,
    errors,
    hasUnacceptedItems,
    dropHandlers
  } = props;
  const [isShowingHelpModal, setIsShowingHelpModal] = useState(false);
  const [isShowingHelpPopover, setIsShowingHelpPopover] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const onButtonClick = () => setIsPopoverOpen(isPopoverOpen => !isPopoverOpen);
  const closePopover = () => setIsPopoverOpen(false);
  let uploadButton;
  const supportedFileTypes = useMemo(() => dropHandlers.map(({
    supportedFiles
  }) => supportedFiles.join(', ')).sort().join(', '), [dropHandlers]);
  const ariaLabels = {
    uploadingFiles: useEuiI18n('euiMarkdownEditorFooter.uploadingFiles', 'Click to upload files'),
    openUploadModal: useEuiI18n('euiMarkdownEditorFooter.openUploadModal', 'Open upload files modal'),
    unsupportedFileType: useEuiI18n('euiMarkdownEditorFooter.unsupportedFileType', 'File type not supported'),
    supportedFileTypes: useEuiI18n('euiMarkdownEditorFooter.supportedFileTypes', 'Supported files: {supportedFileTypes}', {
      supportedFileTypes
    }),
    showSyntaxErrors: useEuiI18n('euiMarkdownEditorFooter.showSyntaxErrors', 'Show errors'),
    showMarkdownHelp: useEuiI18n('euiMarkdownEditorFooter.showMarkdownHelp', 'Show markdown help')
  };
  const syntaxTitle = useEuiI18n('euiMarkdownEditorFooter.syntaxTitle', 'Syntax help');
  const {
    readOnly
  } = useContext(EuiMarkdownContext);
  if (isUploadingFiles) {
    uploadButton = <EuiButtonIcon size="s" iconType={EuiLoadingSpinner} aria-label={ariaLabels.uploadingFiles} isDisabled={readOnly} />;
  } else if (dropHandlers.length > 0 && hasUnacceptedItems) {
    uploadButton = <EuiToolTip content={ariaLabels.supportedFileTypes}>
        <EuiButtonEmpty className="euiMarkdownEditorFooter__uploadError" autoFocus size="s" iconType="paperClip" color="danger" aria-label={`${ariaLabels.unsupportedFileType}. ${ariaLabels.supportedFileTypes}. ${ariaLabels.uploadingFiles}`} onClick={openFiles} isDisabled={readOnly}>
          {ariaLabels.unsupportedFileType}
        </EuiButtonEmpty>
      </EuiToolTip>;
  } else if (dropHandlers.length > 0) {
    uploadButton = <EuiButtonIcon size="s" iconType="paperClip" color="text" aria-label={ariaLabels.openUploadModal} onClick={openFiles} isDisabled={readOnly} />;
  }
  let errorsButton;
  if (errors && errors.length) {
    errorsButton = <EuiPopover button={<EuiButtonEmpty iconType="error" size="s" color="danger" aria-label={ariaLabels.showSyntaxErrors} onClick={onButtonClick} isDisabled={readOnly}>
            {errors.length}
          </EuiButtonEmpty>} isOpen={isPopoverOpen} closePopover={closePopover} panelPaddingSize="s" anchorPosition="upCenter">
        <div className="euiMarkdownEditorFooter__popover">
          <EuiPopoverTitle>
            <EuiI18n token="euiMarkdownEditorFooter.errorsTitle" default="Errors" />
          </EuiPopoverTitle>
          {errors.map((message, idx) => <EuiText size="s" key={idx}>
              {message.toString()}
            </EuiText>)}
        </div>
      </EuiPopover>;
  }
  const uiPluginsWithHelpText = uiPlugins.filter(({
    helpText
  }) => !!helpText);
  const hasUiPluginsWithHelpText = uiPluginsWithHelpText.length > 0;
  const mdSyntaxHref = 'https://guides.github.com/features/mastering-markdown/';
  const mdSyntaxLink = <EuiLink href={mdSyntaxHref} target="_blank">
      <EuiI18n token="euiMarkdownEditorFooter.mdSyntaxLink" default="GitHub flavored markdown" />
    </EuiLink>;
  let helpSyntaxButton;
  if (hasUiPluginsWithHelpText) {
    helpSyntaxButton = <>
        <EuiToolTip content={syntaxTitle}>
          <EuiButtonIcon size="s" className="euiMarkdownEditorFooter__helpButton" iconType={MarkdownLogo} color="text" aria-label={ariaLabels.showMarkdownHelp} onClick={() => setIsShowingHelpModal(!isShowingHelpModal)} isDisabled={readOnly} />
        </EuiToolTip>

        {isShowingHelpModal && <EuiModal onClose={() => setIsShowingHelpModal(false)}>
            <EuiModalHeader>
              <EuiTitle>
                <h1>{syntaxTitle}</h1>
              </EuiTitle>
            </EuiModalHeader>
            <EuiModalBody>
              <EuiText>
                <EuiI18n tokens={['euiMarkdownEditorFooter.syntaxModalDescriptionPrefix', 'euiMarkdownEditorFooter.syntaxModalDescriptionSuffix']} defaults={['This editor uses', 'You can also utilize these additional syntax plugins to add rich content to your text.']}>
                  {([syntaxModalDescriptionPrefix, syntaxModalDescriptionSuffix]) => <p>
                      {syntaxModalDescriptionPrefix} {mdSyntaxLink}.{' '}
                      {syntaxModalDescriptionSuffix}
                    </p>}
                </EuiI18n>
              </EuiText>
              <EuiHorizontalRule />
              {uiPluginsWithHelpText.map(({
            name,
            helpText
          }) => <Fragment key={name}>
                  <EuiTitle size="xxs">
                    <p>
                      <strong>{name}</strong>
                    </p>
                  </EuiTitle>
                  <EuiSpacer size="s" />
                  {helpText}
                  <EuiSpacer size="l" />
                </Fragment>)}
              <EuiHorizontalRule />
            </EuiModalBody>
            <EuiModalFooter>
              <EuiButton onClick={() => setIsShowingHelpModal(false)} fill>
                <EuiI18n token="euiMarkdownEditorFooter.closeButton" default="Close" />
              </EuiButton>
            </EuiModalFooter>
          </EuiModal>}
      </>;
  } else {
    helpSyntaxButton = <EuiPopover button={<EuiButtonIcon title={syntaxTitle} size="s" className="euiMarkdownEditorFooter__helpButton" iconType={MarkdownLogo} color="text" aria-label={ariaLabels.showMarkdownHelp} onClick={() => setIsShowingHelpPopover(!isShowingHelpPopover)} />} isOpen={isShowingHelpPopover} closePopover={() => setIsShowingHelpPopover(false)} panelPaddingSize="s" anchorPosition="upCenter">
        <EuiI18n tokens={['euiMarkdownEditorFooter.syntaxPopoverDescription']} defaults={['This editor uses']}>
          {([syntaxPopoverDescription]) => <p>
              {syntaxPopoverDescription} {mdSyntaxLink}.
            </p>}
        </EuiI18n>
      </EuiPopover>;
  }
  return <div ref={ref} className="euiMarkdownEditorFooter">
      <div className="euiMarkdownEditorFooter__actions">
        {uploadButton}
        {errorsButton}
      </div>
      {helpSyntaxButton}
    </div>;
});
EuiMarkdownEditorFooter.propTypes = {
  uiPlugins: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    button: PropTypes.shape({
      label: PropTypes.string.isRequired,
      iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "at", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "error", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "grabOmnidirectional", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "key", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "namespace", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sparkles", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelineWithArrow", "timelionApp", "timeRefresh", "timeslider", "training", "transitionLeftIn", "transitionLeftOut", "transitionTopIn", "transitionTopOut", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenDenseVector", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired,
      isDisabled: PropTypes.bool
    }).isRequired,
    helpText: PropTypes.node,
    formatting: PropTypes.shape({
      prefix: PropTypes.string,
      suffix: PropTypes.string,
      blockPrefix: PropTypes.string,
      blockSuffix: PropTypes.string,
      multiline: PropTypes.bool,
      replaceNext: PropTypes.string,
      prefixSpace: PropTypes.bool,
      scanFor: PropTypes.string,
      surroundWithNewlines: PropTypes.bool,
      orderedList: PropTypes.bool,
      trimFirst: PropTypes.bool
    }),
    editor: PropTypes.elementType
  }).isRequired).isRequired,
  isUploadingFiles: PropTypes.bool.isRequired,
  openFiles: PropTypes.func.isRequired,
  errors: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.any.isRequired, PropTypes.any.isRequired]).isRequired).isRequired,
  hasUnacceptedItems: PropTypes.bool.isRequired,
  dropHandlers: PropTypes.arrayOf(PropTypes.shape({
    supportedFiles: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    accepts: PropTypes.func.isRequired,
    getFormattingForItem: PropTypes.func.isRequired
  }).isRequired).isRequired
};
EuiMarkdownEditorFooter.displayName = 'EuiMarkdownEditorFooter';
try {
  EuiMarkdownEditorFooter.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/markdown_editor/markdown_editor_footer.tsx',
    description: '',
    displayName: 'EuiMarkdownEditorFooter',
    methods: [],
    props: {
      uiPlugins: {
        defaultValue: null,
        description: '',
        name: 'uiPlugins',
        parent: {
          fileName: 'app/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'EuiMarkdownEditorFooterProps'
        },
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'EuiMarkdownEditorFooterProps'
        }],
        required: true,
        type: {
          name: 'EuiMarkdownEditorUiPlugin<any>[]'
        }
      },
      isUploadingFiles: {
        defaultValue: null,
        description: '',
        name: 'isUploadingFiles',
        parent: {
          fileName: 'app/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'EuiMarkdownEditorFooterProps'
        },
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'EuiMarkdownEditorFooterProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      openFiles: {
        defaultValue: null,
        description: '',
        name: 'openFiles',
        parent: {
          fileName: 'app/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'EuiMarkdownEditorFooterProps'
        },
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'EuiMarkdownEditorFooterProps'
        }],
        required: true,
        type: {
          name: '() => void'
        }
      },
      errors: {
        defaultValue: null,
        description: '',
        name: 'errors',
        parent: {
          fileName: 'app/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'EuiMarkdownEditorFooterProps'
        },
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'EuiMarkdownEditorFooterProps'
        }],
        required: true,
        type: {
          name: 'EuiMarkdownParseError[]'
        }
      },
      hasUnacceptedItems: {
        defaultValue: null,
        description: '',
        name: 'hasUnacceptedItems',
        parent: {
          fileName: 'app/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'EuiMarkdownEditorFooterProps'
        },
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'EuiMarkdownEditorFooterProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      dropHandlers: {
        defaultValue: null,
        description: '',
        name: 'dropHandlers',
        parent: {
          fileName: 'app/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'EuiMarkdownEditorFooterProps'
        },
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor_footer.tsx',
          name: 'EuiMarkdownEditorFooterProps'
        }],
        required: true,
        type: {
          name: 'EuiMarkdownDropHandler[]'
        }
      },
      ref: {
        defaultValue: null,
        description: 'Allows getting a ref to the component instance.\n' + 'Once the component unmounts, React will set `ref.current` to `null` (or call the ref with `null` if you passed a callback ref).\n' + '@see https://react.dev/learn/referencing-values-with-refs#refs-and-the-dom',
        name: 'ref',
        parent: {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'RefAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'RefAttributes'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLDivElement>'
        }
      }
    },
    extendedInterfaces: ['EuiMarkdownEditorFooterProps', 'Attributes']
  };
} catch (e) {}